import dayjs from "lib/dayjs";
import { dayCountFraction, freqToPeriodsPerYear } from "lib/dayCount";

function asDay(value, label) {
  const date = dayjs(value);
  if (!date.isValid()) throw new Error(`${label} is invalid`);
  return date.startOf("day");
}

export function inferRegularCouponSchedule({
  settlementDate,
  maturityDate,
  frequency = "semiannual",
}) {
  const settlement = asDay(settlementDate, "Settlement date");
  const maturity = asDay(maturityDate, "Maturity date");
  if (!maturity.isAfter(settlement, "day")) {
    throw new Error("Maturity date must be after settlement date");
  }
  const periodsPerYear = freqToPeriodsPerYear(frequency);
  const months = 12 / periodsPerYear;
  if (!Number.isInteger(months)) throw new Error("Coupon frequency must divide twelve months");

  const futureDates = [];
  let cursor = maturity;
  while (cursor.isAfter(settlement, "day")) {
    futureDates.push(cursor);
    cursor = cursor.subtract(months, "month");
    if (futureDates.length > 1000) throw new Error("Coupon schedule exceeds supported length");
  }
  futureDates.reverse();
  return {
    settlement,
    maturity,
    periodsPerYear,
    months,
    previousCouponDate: cursor,
    nextCouponDate: futureDates[0],
    futureDates,
  };
}

export function buildMunicipalCashFlows({
  settlementDate,
  maturityDate,
  couponRate,
  frequency = "semiannual",
  dayCount = "US MUNI: 30/360",
  face = 100,
}) {
  let normalizedCoupon = Number(couponRate);
  if (!Number.isFinite(normalizedCoupon) || normalizedCoupon < 0) {
    throw new Error("Coupon rate must be a nonnegative finite number");
  }
  if (normalizedCoupon > 1) normalizedCoupon /= 100;
  const schedule = inferRegularCouponSchedule({ settlementDate, maturityDate, frequency });
  const couponAmount = (face * normalizedCoupon) / schedule.periodsPerYear;
  const accruedFraction = dayCountFraction(
    schedule.previousCouponDate,
    schedule.settlement,
    dayCount
  );
  const accruedInterest = face * normalizedCoupon * accruedFraction;
  const cashFlows = schedule.futureDates.map((date, index) => ({
    date: date.format("YYYY-MM-DD"),
    time: dayCountFraction(schedule.settlement, date, dayCount),
    coupon: couponAmount,
    principal: index === schedule.futureDates.length - 1 ? face : 0,
    amount: couponAmount + (index === schedule.futureDates.length - 1 ? face : 0),
  }));

  return { ...schedule, couponRate: normalizedCoupon, couponAmount, accruedInterest, cashFlows };
}

export function priceCashFlows({ cashFlows, discountFactor, spread = 0 }) {
  return cashFlows.reduce(
    (sum, flow) => sum + flow.amount * discountFactor(flow.time) * Math.exp(-spread * flow.time),
    0
  );
}

export function solveNonCallableOas({
  cleanPrice,
  accruedInterest,
  cashFlows,
  discountFactor,
  lowerBound = -0.1,
  upperBound = 0.1,
  tolerance = 1e-12,
}) {
  const targetDirtyPrice = Number(cleanPrice) + Number(accruedInterest || 0);
  if (!Number.isFinite(targetDirtyPrice) || targetDirtyPrice <= 0) {
    throw new Error("Target price must be positive");
  }
  let low = lowerBound;
  let high = upperBound;
  const value = (spread) => priceCashFlows({ cashFlows, discountFactor, spread });
  if (value(low) < targetDirtyPrice || value(high) > targetDirtyPrice) {
    throw new Error("OAS root is not bracketed");
  }
  for (let iteration = 0; iteration < 100; iteration += 1) {
    const mid = (low + high) / 2;
    const difference = value(mid) - targetDirtyPrice;
    if (Math.abs(difference) <= tolerance) return mid;
    if (difference > 0) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

